import { Polyglot } from 'components/translate';
import { USER_ROLE } from 'configs/constants';
import { isEmpty } from 'helpers/Object';
import { action } from 'mobx';
import { UiService } from 'modules/ui';
import { DeepSearchService, DeepSearchStore } from 'modules/userManager';
import {
  COLUMNS_LIST,
  COLUMNS_LIMIT,
  KNOWN_ERROS,
  USER_STATUS,
  USER_PLUS_ACCESS,
  USER_TYPE
} from 'modules/userManager/constants';

import './UserManagerApi';
import Store from './UserManagerStore';

import Request from '@hotmart/request';

class UserManagerService {
  async search(parameters = {}) {
    try {
      DeepSearchService.saveSearchParameters(Object.assign({}, parameters));

      return await Request('post.search_user', parameters);
    } catch (error) {
      UiService.handleError(error);
    }
  }

  async changeClass(parameters = {}) {
    try {
      return await Request('user.change_class', parameters);
    } catch (error) {
      UiService.handleError(error);

      return Promise.reject(error);
    }
  }

  createSearchParameters(filters) {
    return {
      filters,
      page: Store.page,
      size: Store.perPage,
      sort: this.sortParams
    };
  }

  @action
  async searchUser(dataTableParams) {
    const { isDeepSearch } = DeepSearchStore;

    try {
      let filters = isDeepSearch
        ? [...DeepSearchService.searchParameters.filters]
        : [Object.assign({}, DeepSearchService.simpleFilterParams)];

      filters = filters.map((filter) => {
        if (filter.query) {
          filter.query = filter.query.trim().replace(/\s\s+/g, ' ');
        }
        return filter;
      });

      const response = await this.search(this.createSearchParameters(filters));

      const selectAll = dataTableParams && dataTableParams.selectAll;

      this.setData({ ...response, selectAll });
    } catch (e) {
      UiService.handleError(e);
    }
  }

  @action
  async exportLastSearchAsCSV(userData = {}) {
    try {
      const params = {
        ...userData
      };

      return await Request('post.export_csv', params);
    } catch (e) {
      UiService.handleError(e);
      return Promise.reject(e);
    }
  }

  get sortParams() {
    const sortOptionsParam = [];
    const columnsList = Object.values(COLUMNS_LIST);
    const { sortOptions = {} } = Store;
    const userNameSortOption = COLUMNS_LIST.USER_NAME.sortName;
    const notNameOption = (key) =>
      sortOptions[key] && key !== userNameSortOption;
    const getSortValue = (key) =>
      sortOptions[key] ||
      columnsList.find((c) => c.value === key).defaultOrderBy;
    const filteredSelectedOptions =
      Object.keys(sortOptions).length &&
      Object.keys(sortOptions).filter(notNameOption);
    const firstSelectedOption = filteredSelectedOptions[0];

    if (firstSelectedOption) {
      sortOptionsParam.push(
        `${firstSelectedOption},${getSortValue(firstSelectedOption)}`
      );
      sortOptionsParam.push(
        `&sort=${userNameSortOption},${getSortValue(userNameSortOption)}`
      );
    } else {
      sortOptionsParam.push(
        `${userNameSortOption},${getSortValue(userNameSortOption)}`
      );
    }

    return sortOptionsParam;
  }

  get columnsOnView() {
    const { columns } = Store;
    const columnsList = Object.values(COLUMNS_LIST);

    return [
      COLUMNS_LIST.USER_NAME.sortOption,
      ...columns.map(
        (cName) => columnsList.find((c) => c.value === cName).sortOption
      )
    ].join(',');
  }

  @action
  setData(data = {}) {
    Object.assign(Store, { ...data });
  }

  @action
  changeColumnsView(newColumns) {
    if (Array.isArray(newColumns) && newColumns.length <= COLUMNS_LIMIT) {
      this.setData({ columns: newColumns });
      localStorage.setItem('columns', JSON.stringify(newColumns));
    }
  }

  getRoleLabel(role) {
    const rolesPath = 'users_import.roles';
    const freeStudentLabel = role === USER_ROLE.FREE_STUDENT ? 'student' : null;
    const label =
      !freeStudentLabel &&
      role &&
      (role === 'ADMIN' ? 'administrator' : role.toLowerCase());

    return role ? `${rolesPath}.${freeStudentLabel || label}` : '';
  }

  getStatusLabel(status = '') {
    const accessPath = 'users_manager.deep_search.access.access_options';

    return `${accessPath}.${status.toLowerCase()}`;
  }

  getReasonLabel(reason = '') {
    if (reason) {
      const accessPath = 'users_manager.deep_search.access.reasons_block';

      return `${accessPath}.${reason.toLowerCase()}`;
    }
  }

  getPlusAccessLabel(plusAccessStatus) {
    const accessPlusPath =
      'users_manager.deep_search.access.access_plus_type_user_options';

    return `${accessPlusPath}.${plusAccessStatus.toLowerCase()}`;
  }

  hasPlusAccess(plusAccessStatus) {
    return (
      plusAccessStatus &&
      plusAccessStatus !== USER_PLUS_ACCESS.WITHOUT_PLUS_ACCESS
    );
  }

  userIsActive(userStatus) {
    return userStatus === USER_STATUS.ACTIVE;
  }

  get userTypeList() {
    return [
      {
        value: '',
        label: Polyglot.t('users_manager.users_list.header.tab_all_users')
      },
      ...Object.entries(USER_TYPE).map((userType) => ({
        value: userType[0],
        label: Polyglot.t(userType[1])
      }))
    ];
  }

  @action
  async resendAccess(userId) {
    try {
      if (!userId) {
        throw new Error(`Empty userId. Received: ${userId}`);
      }

      return await Request('user.resend_access', { userId });
    } catch (error) {
      return Promise.reject(error);
    }
  }

  @action
  async changeStatus(data) {
    try {
      const { userId, status } = data;

      if (!(userId && status)) {
        throw new Error(
          `Empty userId or status. Received: ${userId}, ${status}`
        );
      }

      return await Request('user.change_status', data);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  @action
  async delete(userId) {
    try {
      if (!userId) {
        throw new Error(`Empty userId. Received: ${userId}`);
      }

      return await Request('user.delete', { userId });
    } catch (error) {
      return Promise.reject(error);
    }
  }

  @action
  async getUser(userId) {
    try {
      if (!userId) {
        throw new Error(`Empty userId. Received: ${userId}`);
      }

      return await Request('get.user', { userId });
    } catch (error) {
      return Promise.reject(error);
    }
  }

  @action
  async userChangeClass(userData) {
    try {
      const unsentData = isEmpty(userData);

      if (unsentData) {
        throw new Error(`Empty userData. Received: ${userData}`);
      }

      return await Request('user.change_class', userData);
    } catch (error) {
      UiService.handleError(error);

      return Promise.reject(error);
    }
  }

  async fetchUsersManagerConfigs() {
    const { configs } = Store || {};

    if (isEmpty(configs)) {
      try {
        const configs = await Request('get.config');

        this.setData({ configs });
      } catch (error) {
        UiService.handleError(error);
      }
    }
  }

  get resendAccessLimit() {
    const { configs = {} } = Store || {};
    const { maxAllowedBulkSends } = configs || {};

    return maxAllowedBulkSends;
  }

  handleError(error) {
    const { errorCode: aboveLimitErrorCode, label } =
      KNOWN_ERROS.RESEND_ACCESS_ABOVE_LIMIT;

    if (error && error.errorCode === aboveLimitErrorCode.toString()) {
      UiService.setAlertError(label, this.resendAccessLimit);
    } else {
      UiService.handleError(error);
    }
  }

  @action
  async bulkResendAccess(userData) {
    try {
      const unsentData = isEmpty(userData);

      if (unsentData) {
        throw new Error(`Empty userData. Received: ${userData}`);
      }

      return await Request('post.resend_access', userData);
    } catch (error) {
      this.handleError(error);

      return Promise.reject(error);
    }
  }

  @action
  async userChangeStatus(userData) {
    try {
      const unsentData = isEmpty(userData);

      if (unsentData) {
        throw new Error(`Empty userData. Received: ${userData}`);
      }

      return await Request('post.change_status', userData);
    } catch (error) {
      UiService.handleError(error);

      return Promise.reject(error);
    }
  }

  @action
  async userChangeRole(userAreaId, role) {
    try {
      const unsentData = !userAreaId || isEmpty(role);

      if (unsentData) {
        throw new Error(
          `Empty userAreaId or role. Received: ${userAreaId}, ${role}`
        );
      }

      return await Request('user.change_role', { userAreaId, role });
    } catch (error) {
      UiService.handleError(error);

      return Promise.reject(error);
    }
  }

  @action
  async getQuizResult(userAreaHash) {
    try {
      if (!userAreaHash) {
        throw new Error(`Empty userAreaHash. Received: ${userAreaHash}`);
      }
      return await Request('get.resultsByUserArea', { userAreaHash });
    } catch (error) {
      return Promise.reject(error);
    }
  }

  @action
  async updatePurchaseDate(userAreaId, purchaseDate) {
    try {
      if (!userAreaId) {
        throw new Error(`Empty userAreaHash. Received: ${userAreaId}`);
      }

      return await Request('user.update_purchase_date', {
        userAreaId,
        purchaseDate
      });
    } catch (error) {
      UiService.handleError(error);

      return Promise.reject(error);
    }
  }

  @action
  userHistorics(userId, params) {
    try {
      if (!userId) {
        throw new Error(`Empty userId. Received: ${userId}`);
      }

      return Request('get.user_history', { userId, ...params });
    } catch (error) {
      return Promise.reject(error);
    }
  }

  getContentList(userId) {
    try {
      if (!userId) {
        throw new Error(`Empty userId. Received: ${userId}`);
      }

      return Request('get.lessons', { userId });
    } catch (error) {
      UiService.handleError(error);

      return Promise.reject(error);
    }
  }
}

export default new UserManagerService();
